import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </Link>

        <article className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-p:text-muted-foreground prose-li:text-muted-foreground">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Privacy Policy — ReviewBoost Pro Manager
          </h1>
          <p className="text-sm text-muted-foreground">Effective date: 5 March 2026 · Last updated: 8 March 2026</p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">1. Scope and data controller</h2>
          <p>
            The data controller responsible for your personal data is the entity operating ReviewBoost Pro Manager
            (app.reviewboost24.com). For the purposes of this policy, &quot;we,&quot; &quot;us,&quot; and &quot;our&quot;
            refer to that entity. Contact: support@reviewboost24.com.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">2. Data we collect</h2>
          <p>
            <strong>Account and identity:</strong> When you sign in with Google OAuth, we receive your name, email
            address, and profile picture. We use this to verify your identity and to personalise your experience (e.g.
            greetings).
          </p>
          <p>
            <strong>Business data:</strong> We store sales, inventory, expenses, and related data that you enter or
            upload. This may include product names, quantities, amounts in Zambian Kwacha (ZMW), and similar
            operational data. We use it solely to provide analytics, reports, and AI-driven insights.
          </p>
          <p>
            <strong>Technical and usage data:</strong> We may collect device and browser information, IP address, and
            usage patterns (e.g. pages visited) to operate the service, prevent abuse, and improve the product. We do
            not sell this data to third parties for advertising.
          </p>
          <p>
            <strong>Payment and billing data:</strong> Subscription payments are processed by Lenco, our payment
            partner. We do not store full card numbers or sensitive payment credentials. Lenco processes payment data
            in accordance with its own privacy policy and applicable regulatory requirements.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">3. Legal basis and purposes</h2>
          <p>
            We process your data on the basis of contract performance (to provide the service you signed up for),
            legitimate interests (security, product improvement), and where applicable, consent. We use your data only
            to provide Strategic Directives, financial analytics, inventory and team management, and support. We do not
            sell your personal or business data to third parties.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">4. Third-party processors and sub-processors</h2>
          <p>
            We use trusted service providers who process data on our behalf: Google (authentication), Supabase
            (hosting and database), Vercel (hosting), and Lenco (payment processing—see{" "}
            <a href="https://lenco.co/zm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">lenco.co/zm</a>).
            These processors are bound by data processing terms and use data only as necessary to provide the service.
            We do not share your data with advertisers or data brokers.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">5. International transfers</h2>
          <p>
            Your data may be processed in jurisdictions outside Zambia (e.g. EU/US) where our providers operate. We
            ensure appropriate safeguards (e.g. standard contractual clauses or equivalent) where required by
            applicable law.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">6. Retention</h2>
          <p>
            We retain account and business data for as long as your account is active and as needed to provide the
            service and comply with legal obligations. After account closure or deletion request, we delete or
            anonymise personal and business data within a reasonable period, except where we must retain it for law
            or dispute resolution.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">7. Your rights</h2>
          <p>
            Depending on your location, you may have the right to: access your data, correct inaccuracies, request
            erasure, restrict processing, data portability, object to processing, and withdraw consent. You may also
            have the right to lodge a complaint with a supervisory authority. To exercise these rights, contact us at
            support@reviewboost24.com. We will respond within a reasonable period (e.g. 30 days where required by
            law).
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">8. Cookies and similar technologies</h2>
          <p>
            We use strictly necessary and functional cookies (and similar technologies) to keep you signed in and to
            remember preferences (e.g. theme). We do not use cookies for third-party advertising or cross-site
            tracking for ads.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">9. Security</h2>
          <p>
            We implement appropriate technical and organisational measures (e.g. encryption, access controls, secure
            authentication) to protect your data against unauthorised access, loss, or misuse.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">10. Children</h2>
          <p>
            The service is not directed at individuals under 18. We do not knowingly collect personal data from
            children. If you believe we have collected such data, please contact us so we can delete it.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">11. Platform disclaimer</h2>
          <p>
            ReviewBoost Pro Manager is a software platform and is not a bank, payment processor, or financial
            institution. All financial transactions are conducted through Lenco and are subject solely to Lenco&apos;s
            regulatory compliance and licensing.
          </p>

          <h2 className="mt-8 text-lg font-semibold text-foreground">12. Changes and contact</h2>
          <p>
            We may update this policy from time to time. We will post the updated version on this page and update the
            &quot;Last updated&quot; date. Continued use of the service after changes constitutes acceptance of the
            updated policy. For privacy-related inquiries: support@reviewboost24.com; app: https://app.reviewboost24.com.
          </p>
        </article>
      </div>
    </div>
  );
}
